/**
 * BranchTerminalPanel — what a branch that is no longer a live forecast shows where its
 * distribution used to be.
 *
 * A branch whose premise has been graded is no longer a forecast: the assumption it was
 * built on is now a fact, either the one the user asserted or its opposite. So the slot at
 * the branch's resolution point carries a statement and a single action instead of bars —
 * and the branch's downstream triggers are dropped, because those were elicited under an
 * assumption the world has since answered.
 *
 * ── One layout, five states ─────────────────────────────────────────────────
 * Confirmed, contradicted, superseded, expired and undecided share this component and differ
 * only in the
 * headline, tint and body. That is deliberate: the acknowledge-and-clear gesture a user
 * learns on one state has to exist on the others, or they learn it once and then find it
 * missing — which is also why `clearable: false` DISABLES the button with a reason rather
 * than dropping it.
 *
 * ── EXPIRED vs UNDECIDED: both are "no answer", for opposite reasons ────────
 * Expired means nobody ever asked — no contract was minted, so no verdict was possible.
 * Undecided means someone asked and the answer came back inconclusive (a `resolved_tie` or
 * `unresolved` verdict). Neither may claim the trigger occurred or did not, but they must
 * not be collapsed either: one is a gap in the machinery, the other is a finding about a
 * genuinely ambiguous world, and a user who sees "could not be decided" on a trigger nobody
 * checked has been told something false about what we did.
 *
 * ── EXPIRED is a fact about the CLOCK, not the trigger ──────────────────────
 * The premise's window shut with nothing able to settle it — no contract was ever minted,
 * so no verdict was ever asked for, let alone given. It shares superseded's careful shape
 * for the same reason: it must claim neither occurrence nor non-occurrence. "The trigger
 * did not occur" would be a finding, and there was no finding — only a deadline. See
 * `branchPremise.ts::premiseNeverSettles`.
 *
 * ── SUPERSEDED is a fact about the TRUNK, not the trigger ───────────────────
 * The root's question resolved while this branch's premise never settled either way, so
 * the conditional has nothing left to be a forecast of. The copy must therefore claim
 * neither of the two things that did NOT happen: the premise was not graded, and this
 * branch's own question did not resolve. See `branchPremise.ts::branchSuperseded` — and
 * note nothing about this state is persisted, so a late genuine grading still arrives and
 * still wins.
 *
 * ── Why the copy is conditional ─────────────────────────────────────────────
 * "…is now incorporated in the mainline forecast" is a claim about the TRUNK's number, and
 * it is only true once the trunk has re-forecast since the premise was graded. Three clocks
 * are involved and only two are coupled — the trigger's window, the resolver's post-close
 * ladder, and the trunk's cadence — so on a weekly forecast this can sit
 * graded-but-not-yet-incorporated for days. `incorporated` comes from
 * `branchPremise.premiseIncorporated`; when it is false the panel says so in the future
 * tense rather than overclaiming. See that function's header.
 *
 * Pure presentational — props in, no store reads.
 */

import { useState } from "react";
import { premiseClearMessage } from "../lib/forecast/premiseClearMessage.js";
import { ui } from "../lib/telemetry/instrument.js";
import { Button } from "./ui/Primitives.jsx";

/**
 * @param {object}   props
 * @param {"confirmed"|"contradicted"|"superseded"|"expired"|"undecided"} props.state
 *   graded premise, trunk resolved, a window that closed unsettled, or an inconclusive verdict
 * @param {boolean}  props.incorporated  has the trunk re-forecast since grading?
 * @param {object}   props.C             raw-hex palette
 * @param {Function} props.onClear       acknowledge + hide the lane (never deletes); may
 *   return (a promise of) `{ok, status}` — a refusal is then shown under the button
 * @param {boolean}  [props.busy]        clear in flight — disables the button
 * @param {boolean}  [props.clearable]   would the server accept a clear for this premise?
 */
// The panel's OUTER width — the 300px content box plus its 13px side padding and 1px
// border — for callers that keep it clear of something (ForecastLane, the Chat Console).
export const BRANCH_TERMINAL_PANEL_OUTER_W = 300 + 2 * 13 + 2;

export default function BranchTerminalPanel({
  state,
  incorporated,
  C,
  onClear,
  busy = false,
  clearable = true,
}) {
  const confirmed = state === "confirmed";
  const superseded = state === "superseded";
  const expired = state === "expired";
  const undecided = state === "undecided";
  const headline = expired
    ? "This trigger's window has closed"
    : undecided
      ? "Sayer couldn't tell whether it happened"
      : superseded
        ? "The main forecast has resolved"
        : confirmed
          ? "It happened as you assumed"
          : "It didn't happen as you assumed";
  const body = expired
    ? "This scenario is no longer live. Its window passed with no way to check whether the trigger happened, so it has no outcome of its own."
    : undecided
      ? "This scenario is no longer live. Sayer checked the trigger but couldn't settle it either way, so it has no outcome of its own."
      : superseded
        ? "This scenario is no longer live. Its trigger never settled either way, so it has no outcome of its own."
        : confirmed
          ? incorporated
            ? "The main forecast now includes this."
            : "The main forecast will include this at its next update."
          : "This scenario can no longer happen. The main forecast is unaffected.";
  // The clear's own round trip. Tracked here so the click always visibly does something:
  // "Clearing…" while it runs, then either the lane goes (success) or a line saying why not.
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = busy || pending;
  const disabled = inFlight || !clearable;
  const handleClear = async () => {
    setError(null);
    setPending(true);
    try {
      const res = await onClear?.();
      if (res && res.ok === false) setError(premiseClearMessage(res.status));
    } catch {
      setError(premiseClearMessage(null));
    } finally {
      setPending(false);
    }
  };
  return (
    <div
      style={{
        background: C.btnBg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: "11px 13px",
        width: 300,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.3,
          color: confirmed ? C.blueTeam : C.white,
        }}
      >
        {headline}
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.5, color: C.grayL, marginTop: 8 }}>{body}</div>
      <Button
        {...ui("branch-terminal.clear", state)}
        size="sm"
        disabled={disabled}
        onClick={handleClear}
        style={{ marginTop: 12 }}
      >
        {inFlight ? "Clearing…" : "Clear scenario"}
      </Button>
      {/* Why the gesture is greyed. The server refuses a clear unless the premise is
          WRITTEN as terminal or the root has resolved (one undifferentiated 409 for every
          miss, so there is nothing better to say on failure); loosening it further would
          let the user hide a branch still awaiting a verdict. That leaves one narrow state
          here — a premise this browser has worked out is terminal but no sweep has stored
          yet. Enabling the button there would teach a gesture that fails; dropping it would
          make a learned control vanish. State the precondition instead. */}
      {error && !inFlight && (
        <div role="alert" style={{ fontSize: 12, lineHeight: 1.4, color: C.redTeam, marginTop: 8 }}>
          {error}
        </div>
      )}
      {!clearable && !inFlight && (
        <div style={{ fontSize: 12, lineHeight: 1.4, color: C.gray, marginTop: 8 }}>
          You can clear this once the trigger settles either way.
        </div>
      )}
    </div>
  );
}
